<template>
  <div class="history-data-view" id="monnitorHistory">
    <!-- 查询栏 -->
    <div class="compact-query-bar">
      <div class="query-section">
        <!-- 状态提示徽章 -->
        <div
          :class="[
            'status-badge-tip',
            robotStatus === 'online' ? 'online-badge' : 'offline-badge',
          ]"
        >
          <Icon
            :type="
              robotStatus === 'online'
                ? 'ios-checkmark-circle'
                : 'ios-close-circle'
            "
          />
          <span class="badge-text">
            {{ robotStatus === 'online' ? langMap.dashboard_onlineTip[lang] : langMap.dashboard_offlineTip[lang] }}
          </span>
        </div>

        <!-- 监控类型切换 -->
        <div class="monitor-type-tabs">
          <div
            v-for="type in monitorTypes"
            :key="type.value"
            :class="['type-tab', { active: monitorType === type.value }]"
            @click="handleTypeChange(type.value)"
          >
            <span class="tab-icon">{{ type.icon }}</span>
            <span class="tab-text">{{ langMap[type.label][lang] }}</span>
          </div>
        </div>
      </div>

      <div class="query-filters">
        <div class="filter-row">
          <!-- 时间范围（所有类型都有） -->
          <div class="filter-item">
            <span class="filter-label">{{ langMap.dashboard_timeRange[lang] }}</span>
            <DatePicker
              v-model="timeRange"
              type="datetimerange"
              :placeholder="langMap.dashboard_selectTimeRangePlaceholder[lang]"
              format="yyyy-MM-dd HH:mm"
              :options="dateOptions"
              style="width: 340px"
              size="small"
            />
          </div>

          <!-- 设备选择（所有类型都有） -->
          <div class="filter-item">
            <span class="filter-label">{{ langMap.dashboard_device[lang] }}</span>
            <Select
              v-model="queryParams.deviceId"
              :placeholder="langMap.dashboard_selectDevice[lang]"
              clearable
              style="width: 200px"
              size="small"
            >
              <Option
                v-for="device in deviceList"
                :key="device.deviceId"
                :value="device.deviceId"
              >
                {{ device[`deviceName${lang === 'zh' ? 'Cn' : 'En'}`] }}
              </Option>
            </Select>
          </div>

          <!-- 在线状态（仅设备在线类型） -->
          <div class="filter-item" v-if="monitorType === 'device_online'">
            <span class="filter-label">{{ langMap.dashboard_statusLabel[lang] }}</span>
            <Select
              v-model="queryParams.status"
              :placeholder="langMap.dashboard_statusLabel[lang]"
              clearable
              style="width: 120px"
              size="small"
            >
              <Option :value="0">{{ langMap.dashboard_online[lang] }}</Option>
              <Option :value="3">{{ langMap.dashboard_disconnected[lang] }}</Option>
            </Select>
          </div>

          <!-- 错误码（设备错误和运行错误） -->
          <div class="filter-item" v-if="monitorType !== 'device_online'">
            <span class="filter-label">{{ langMap.errorCode[lang] }}</span>
            <Select
              v-model="queryParams.errCode"
              :placeholder="langMap.dashboard_selectErrorCode[lang]"
              clearable
              style="width: 200px"
              size="small"
            >
              <Option
                v-for="error in errorCodeList"
                :key="error.errCode"
                :value="error.errCode"
              >
                {{ error.errCode }} -
                {{ error[`errName${lang === 'zh' ? 'Cn' : 'En'}`] }}
              </Option>
            </Select>
          </div>

          <!-- 错误等级（设备错误和运行错误） -->
          <div class="filter-item" v-if="monitorType !== 'device_online'">
            <span class="filter-label">{{ langMap.dashboard_errorDesc[lang] }}</span>
            <Select
              v-model="queryParams.errLevel"
              :placeholder="langMap.dashboard_errorDesc[lang]"
              clearable
              style="width: 120px"
              size="small"
            >
              <Option :value="0">{{ langMap.dashboard_levelRecovered[lang] }}</Option>
              <Option :value="1">{{ langMap.dashboard_levelWarn[lang] }}</Option>
              <Option :value="2">{{ langMap.dashboard_levelError[lang] }}</Option>
              <Option :value="3">{{ langMap.dashboard_levelFatal[lang] }}</Option>
            </Select>
          </div>

          <!-- 查询按钮 -->
          <div class="filter-actions">
            <Button
              type="primary"
              size="small"
              @click="handleQuery(true)"
              :loading="loading"
            >
              <Icon type="ios-search" /> {{ langMap.dashboard_search[lang] }}
            </Button>
            <Button size="small" @click="handleReset">
              <Icon type="ios-refresh" /> {{ langMap.dashboard_reset[lang] }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- 数据卡片时间轴 -->
    <div class="data-cards-wrapper">
      <div v-if="!loading && tableData.length === 0" class="empty-state">
        <div class="empty-icon-wrapper">
          <div class="hologram-ring"></div>
          <div class="hologram-ring delay-1"></div>
          <div class="hologram-ring delay-2"></div>
          <div class="empty-icon">📊</div>
        </div>
        <div class="empty-text">{{ langMap.dashboard_noData[lang] }}</div>
      </div>

      <div v-else class="timeline-container">
        <Spin v-if="loading" fix>
          <div class="loading-text">
            <Icon type="ios-loading" size="30" class="spin-icon-load"></Icon>
            <div>{{ langMap.dashboard_loading[lang] }}</div>
          </div>
        </Spin>

        <div
          v-for="(item, index) in tableData"
          :key="index"
          class="data-card"
          :class="getCardClass(item)"
        >
          <!-- 时间点 -->
          <div class="time-dot"></div>

          <!-- 卡片内容 -->
          <div class="card-content">
            <!-- 时间标签 -->
            <div class="card-time">
              <Icon type="ios-time-outline" />
              <span class="time-text">{{ formatTime(item.dataTime) }}</span>
            </div>

            <div class="card-header">
              <div class="device-info">
                <div class="device-icon">
                  <svg-icon :name="getDeviceIcon(item.deviceName)" />
                </div>
                <div class="device-details">
                  <span class="device-name">{{ getDeviceName(item) }}</span>
                  <span class="device-id"
                    >ID: {{ item.deviceId
                    }}<template v-if="item.deviceName">
                      | {{ item.deviceName }}</template
                    ></span
                  >
                </div>
              </div>

              <!-- 设备在线状态显示 -->
              <span
                v-if="monitorType === 'device_online'"
                :class="[
                  'status-badge',
                  item.status === 0 ? 'status-online' : 'status-offline',
                ]"
              >
                <span class="status-dot"></span>
                {{ item.status === 0 ? langMap.dashboard_online[lang] : langMap.dashboard_disconnected[lang] }}
              </span>

              <!-- 错误等级显示 -->
              <span
                v-else
                :class="['level-badge', getLevelClass(item.errLevel)]"
              >
                {{ getLevelText(item.errLevel) }}
              </span>
            </div>

            <!-- 错误信息 (仅错误类型显示) -->
            <div v-if="monitorType !== 'device_online'" class="card-body">
              <div class="error-row">
                <span class="error-label">{{ langMap.errorCode[lang] }}:</span>
                <p class="error-code">{{ item.errCode }}</p>
                <span class="error-divider">|</span>
                <span class="error-desc">{{ getErrorDesc(item) }}</span>
              </div>
              <div
                v-if="item.value !== null && item.value !== undefined"
                class="error-row"
              >
                <span class="error-label">{{ langMap.dashboard_paramValueLabel[lang] }}:</span>
                <span class="error-value">{{ item.value }}</span>
              </div>
            </div>
          </div>

          <!-- 连接线 -->
          <div v-if="index < tableData.length - 1" class="timeline-line"></div>
        </div>
      </div>
    </div>

    <!-- 自定义分页器 -->
    <div class="pagination-wrapper" v-if="!loading && tableData.length > 0">
      <div class="cyber-pagination">
        <!-- 总数信息 -->
        <div class="pagination-info">
          <span class="info-label">{{ langMap.dashboard_totalRecords[lang] }}</span>
          <span class="info-value">{{ total }}</span>
          <span class="info-divider">|</span>
          <span class="info-text">{{ pageRangeText }}</span>
        </div>

        <!-- 页码按钮组 -->
        <div class="pagination-controls">
          <!-- 首页 -->
          <button
            class="page-btn page-first"
            :disabled="queryParams.index === 1"
            @click="handlePageChange(1)"
          >
            <span class="btn-icon">《《</span>
          </button>

          <!-- 上一页 -->
          <button
            class="page-btn page-prev"
            :disabled="queryParams.index === 1"
            @click="handlePageChange(queryParams.index - 1)"
          >
            <span class="btn-icon">‹</span>
          </button>

          <!-- 页码按钮 -->
          <div class="page-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="[
                'page-number',
                {
                  active: page === queryParams.index,
                  ellipsis: page === '...',
                },
              ]"
              :disabled="page === '...'"
              @click="page !== '...' && handlePageChange(page)"
            >
              <span
                class="number-glow"
                v-if="page === queryParams.index"
              ></span>
              {{ page }}
            </button>
          </div>

          <!-- 下一页 -->
          <button
            class="page-btn page-next"
            :disabled="queryParams.index === totalPages"
            @click="handlePageChange(queryParams.index + 1)"
          >
            <span class="btn-icon">›</span>
          </button>

          <!-- 尾页 -->
          <button
            class="page-btn page-last"
            :disabled="queryParams.index === totalPages"
            @click="handlePageChange(totalPages)"
          >
            <span class="btn-icon">》》</span>
          </button>
        </div>

        <!-- 每页条数选择 -->
        <div class="page-size-selector">
          <span class="selector-label">{{ langMap.dashboard_perPage[lang] }}</span>
          <div class="size-options">
            <button
              v-for="size in pageSizeOptions"
              :key="size"
              :class="['size-option', { active: queryParams.size === size }]"
              @click="handlePageSizeChange(size)"
            >
              {{ size }}
            </button>
          </div>
        </div>

        <!-- 快速跳转 -->
        <div class="page-jumper">
          <span class="jumper-label">{{ langMap.dashboard_jumpTo[lang] }}</span>
          <input
            type="number"
            class="jumper-input"
            v-model.number="jumpPage"
            @keyup.enter="handleJumpPage"
            :placeholder="String(queryParams.index)"
            min="1"
            :max="totalPages"
          />
          <button class="jumper-btn" @click="handleJumpPage">
            <span class="btn-arrow">→</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { buildQueryParams } from "@/libs/util";
import langMap from '@/utils/language';
import { mapState } from 'vuex';

export default {
  props: {
    robotCode: {
      type: String,
      required: true,
    },
    activeTab: {
      type: String,
      default: "device_online",
    },
    robotStatus: {
      type: String,
      default: "offline", // 'online' 或 'offline'
    },
  },
  data() {
    return {
      langMap:langMap,
      showTip: true,
      loading: false,
      timeRange: [],
      // 监控类型
      monitorType: "device_online",
      monitorTypes: [
        { value: "device_online", label: "dashboard_deviceOfflineStatus", icon: "📶" },
        { value: "device_error", label: "dashboard_deviceErrorTab", icon: "⚠️" },
        { value: "running_error", label: "dashboard_runningErrorTab", icon: "❌" },
      ],
      queryParams: {
        robotCode: this.robotCode,
        startTime: null,
        endTime: null,
        deviceId: "",
        errCode: "",
        status: null,
        errLevel: null,
        index: 1,
        size: 20,
      },
      tableData: [],
      total: 0,
      pageSizeOptions: [10, 20, 30, 50],
      jumpPage: null,
      // 设备列表
      deviceList: [],
      // 错误码列表
      errorCodeList: [],
    };
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    // 日期选择器配置
    dateOptions() {
      return {
        disabledDate: (date) => {
          // 禁用未来时间
          const now = new Date();
          if (date > now) {
            return true;
          }

          // 禁用2个月以前的时间
          const twoMonthsAgo = new Date();
          twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
          if (date < twoMonthsAgo) {
            return true;
          }

          // 如果已经选择了开始或结束时间，限制范围为7天
          if (this.timeRange && this.timeRange.length > 0) {
            const selectedDate = this.timeRange[0];
            if (selectedDate) {
              const maxDiff = 7 * 24 * 3600 * 1000; // 7天的毫秒数
              const diff = Math.abs(date.getTime() - selectedDate.getTime());
              if (diff > maxDiff) {
                return true;
              }
            }
          }

          return false;
        },
        shortcuts: [
          {
            text: this.langMap.dashboard_last1Hour[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last3Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 3);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last6Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 6);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last24Hours[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24);
              return [start, end];
            },
          },
          {
            text: this.langMap.dashboard_last7Days[this.lang],
            value() {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              return [start, end];
            },
          },
        ],
      };
    },
    // 总页数
    totalPages() {
      return Math.ceil(this.total / this.queryParams.size) || 1;
    },
    // 当前页范围文本
    pageRangeText() {
      const start = (this.queryParams.index - 1) * this.queryParams.size + 1;
      const end = Math.min(
        this.queryParams.index * this.queryParams.size,
        this.total
      );
      return `${start}-${end}`;
    },
    // 可见页码
    visiblePages() {
      const current = this.queryParams.index;
      const total = this.totalPages;
      const pages = [];

      if (total <= 7) {
        // 总页数小于等于7，显示所有页码
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // 总页数大于7，智能显示
        if (current <= 4) {
          // 当前页在前4页
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(total);
        } else if (current >= total - 3) {
          // 当前页在后4页
          pages.push(1);
          pages.push("...");
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          // 当前页在中间
          pages.push(1);
          pages.push("...");
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(total);
        }
      }

      return pages;
    },
  },
  watch: {
    robotCode(newCode) {
      this.queryParams.robotCode = newCode;
      this.handleReset();
    },
    timeRange(newVal) {
      // 当 timeRange 通过 v-model 变化时，重新计算时间戳
      if (newVal && newVal.length === 2) {
        this.handleTimeChange(newVal);
      } else {
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    },
  },
  mounted() {
    // 默认查询3小时
    const end = new Date();
    const start = new Date();
    start.setTime(start.getTime() - 3600 * 1000 * 3);
    this.timeRange = [start, end];
    this.handleTimeChange(this.timeRange);

    // 加载字典
    this.loadDeviceList();
    this.loadErrorCodeList();
  },
  methods: {
    handleTimeChange(dates) {
      if (dates && dates.length === 2) {
        // 验证时间范围是否超过7天
        const diff = dates[1].getTime() - dates[0].getTime();
        const maxDiff = 7 * 24 * 3600 * 1000; // 7天

        if (diff > maxDiff) {
          this.$Message.warning(this.langMap.dashboard_timeRangeMaxOneWeek[this.lang]);
          this.timeRange = [];
          this.queryParams.startTime = null;
          this.queryParams.endTime = null;
          return;
        }

        this.queryParams.startTime = dates[0].getTime();
        this.queryParams.endTime = dates[1].getTime();
      } else {
        this.queryParams.startTime = null;
        this.queryParams.endTime = null;
      }
    },

    // 加载设备列表
    loadDeviceList() {
      api
        .getData("system-server/monitor/dict/device/list", this)
        .then((res) => {
          if (res.state == 1) {
            this.deviceList = (res.data || []).filter(j => j.deviceType === 1);
          }
        })
        .catch((err) => {
          console.error("加载设备列表失败", err);
        });
    },

    // 加载错误码列表
    loadErrorCodeList() {
      api
        .getData("system-server/monitor/dict/errorCode/list", this)
        .then((res) => {
          if (res.state == 1) {
            this.errorCodeList = res.data || [];
          }
        })
        .catch((err) => {
          console.error("加载错误码列表失败", err);
        });
    },

    // 切换监控类型
    handleTypeChange(type) {
      if (this.monitorType === type) return;
      this.monitorType = type;
      this.handleReset();
    },

    handleQuery(resetPage = false) {
      if (!this.queryParams.startTime || !this.queryParams.endTime) {
        this.$Message.warning(this.langMap.dashboard_pleaseSelectTimeRange[this.lang]);
        return;
      }

      // 只有主动查询时才重置到第1页和默认每页数量
      if (resetPage) {
        this.queryParams.index = 1;
        this.queryParams.size = 20;
      }

      this.loading = true;
      let apiPath = "";

      switch (this.monitorType) {
        case "device_online":
          apiPath = "notice-server/monitor/record/device/online/page";
          break;
        case "device_error":
          apiPath = "notice-server/monitor/record/device/error/page";
          break;
        case "running_error":
          apiPath = "notice-server/monitor/record/running/error/page";
          break;
      }

      // 构建 URL 参数
      const queryString = buildQueryParams(this.queryParams);
      const fullPath = `${apiPath}?${queryString}`;

      api
        .getData(fullPath, this)
        .then((res) => {
          this.loading = false;
          if (res.state == 0) {
            this.$Message.error(res.msg || this.langMap.dashboard_queryFailed[this.lang]);
          } else {
            this.tableData = res.data.rows || [];
            this.total = res.data.total || 0;
            // 服务器如果没有返回 current，就保持当前的 queryParams.index 不变
            // 不需要重新赋值，因为 queryParams.index 已经在 handlePageChange 中设置过了
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    handleReset() {
      this.queryParams = {
        robotCode: this.robotCode,
        startTime: this.queryParams.startTime,
        endTime: this.queryParams.endTime,
        deviceId: "",
        errCode: "",
        status: null,
        errLevel: null,
        index: 1,
        size: 20,
      };
      this.tableData = [];
      this.total = 0;
    },

    handlePageChange(page) {
      this.queryParams.index = page;
      this.handleQuery();
    },

    handlePageSizeChange(size) {
      this.queryParams.size = size;
      this.queryParams.index = 1;
      this.handleQuery();
    },

    formatTime(timestamp) {
      if (!timestamp) return "--";
      const date = new Date(timestamp);
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },

    getDeviceName(item) {
      return item[`deviceName${this.lang === 'zh' ? 'Cn' : 'En'}`];
    },

    getErrorDesc(item) {
      return item[`errCode${this.lang === 'zh' ? 'Cn' : 'En'}`];
    },

    getLevelText(level) {
      const map = {
        0: this.langMap.dashboard_levelRecovered[this.lang],
        1: this.langMap.dashboard_levelWarn[this.lang],
        2: this.langMap.dashboard_levelError[this.lang],
        3: this.langMap.dashboard_levelFatal[this.lang],
      };
      return map[level] || this.langMap.dashboard_levelUnknown[this.lang];
    },

    getLevelClass(level) {
      const map = {
        0: "level-recovered",
        1: "level-warn",
        2: "level-error",
        3: "level-fatal",
      };
      return map[level] || "";
    },

    getCardClass(item) {
      if (this.monitorType === "device_online") {
        return item.status === 0 ? "card-online" : "card-offline";
      } else {
        const levelMap = {
          0: "card-recovered",
          1: "card-warn",
          2: "card-error",
          3: "card-fatal",
        };
        return levelMap[item.errLevel] || "";
      }
    },

    getDeviceIcon(deviceName) {
      // 根据设备名称返回svg-icon的icon-class
      if (!deviceName) return null;

      const name = deviceName.toLowerCase();

      if (name.includes("battery")) return "battery";
      if (name.includes("motor")) return "motor";
      if (name.includes("camera")) return "camera";
      if (name.includes("fix")) return "fix_";
      if (name.includes("imu")) return "RTU";
      if (name.includes("lidar")) return "lidar_";
      if (name.includes("rtk")) return "RTK";
      return "module"; // 其他设备使用emoji退回
    },

    // 快速跳转页面
    handleJumpPage() {
      if (
        !this.jumpPage ||
        this.jumpPage < 1 ||
        this.jumpPage > this.totalPages
      ) {
        this.$Message.warning(this.langMap.dashboard_inputValidPageRange[this.lang].replace('{max}', this.totalPages));
        return;
      }
      this.handlePageChange(this.jumpPage);
      this.jumpPage = null;
    },
  },
};
</script>

<style lang="less" scoped>
#monnitorHistory {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
  overflow: hidden;

  // 监控类型标签页
  .monitor-type-tabs {
    display: flex;
    gap: 8px;

    .type-tab {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      background: rgba(0, 30, 60, 0.4);
      border: 1px solid rgba(0, 240, 255, 0.3);
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;

      .tab-icon {
        font-size: 16px;
        filter: grayscale(0.5);
        transition: filter 0.3s ease;
      }

      .tab-text {
        font-weight: 500;
      }

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 0;
        height: 100%;
        background: linear-gradient(90deg,
            transparent,
            rgba(0, 240, 255, 0.2));
        transition: width 0.3s ease;
      }

      &:hover {
        border-color: rgba(0, 240, 255, 0.6);
        color: #fff;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 240, 255, 0.3);

        .tab-icon {
          filter: grayscale(0);
        }

        &::before {
          width: 100%;
        }
      }

      &.active {
        background: linear-gradient(135deg,
            rgba(0, 240, 255, 0.25),
            rgba(0, 180, 255, 0.2));
        border-color: #00f0ff;
        color: #00f0ff;
        box-shadow: 0 0 15px rgba(0, 240, 255, 0.4),
          inset 0 0 10px rgba(0, 240, 255, 0.1);
        font-weight: 600;

        .tab-icon {
          filter: grayscale(0) drop-shadow(0 0 4px rgba(0, 240, 255, 0.8));
        }

        .tab-text {
          text-shadow: 0 0 8px rgba(0, 240, 255, 0.6);
        }
      }
    }
  }

  .data-cards-wrapper {
    height: ~"calc(100% - 215px)";
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;

    .loading-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      color: #00f0ff;
      font-size: 16px;

      .spin-icon-load {
        animation: spin 1s linear infinite;
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 300px;
      gap: 20px;

      .empty-icon-wrapper {
        position: relative;
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;

        .hologram-ring {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 2px solid rgba(0, 240, 255, 0.3);
          border-radius: 50%;
          animation: ringPulse 2s ease-in-out infinite;

          &.delay-1 {
            animation-delay: 0.3s;
          }

          &.delay-2 {
            animation-delay: 0.6s;
          }
        }

        .empty-icon {
          font-size: 60px;
          opacity: 0.5;
          position: relative;
          z-index: 1;
          animation: iconFloat 3s ease-in-out infinite;
        }
      }

      .empty-text {
        color: rgba(255, 255, 255, 0.7);
        font-size: 18px;
        font-weight: 600;
        text-shadow: 0 0 10px rgba(0, 240, 255, 0.3);
      }

      .empty-subtext {
        color: rgba(0, 240, 255, 0.6);
        font-size: 14px;
        text-align: center;
        max-width: 400px;
        line-height: 1.6;
      }
    }

    .timeline-container {
      position: relative;
      padding-left: 30px;
      padding-right: 10px;
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding-bottom: 20px;
      margin-bottom: 5px;
      min-height: 0;

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: rgba(0, 30, 60, 0.3);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 240, 255, 0.3);
        border-radius: 4px;

        &:hover {
          background: rgba(0, 240, 255, 0.5);
        }
      }
    }

    .data-card {
      position: relative;
      margin-bottom: 20px;
      transition: all 0.3s ease;

      &:hover {
        transform: translateX(5px);

        .card-content {
          box-shadow: 0 0 25px rgba(0, 240, 255, 0.4);
        }
      }

      // 时间线上的点
      .time-dot {
        position: absolute;
        left: -24px;
        top: 8px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #00f0ff;
        box-shadow: 0 0 10px rgba(0, 240, 255, 0.8);
        animation: pulse 2s ease-in-out infinite;
        z-index: 10;
      }

      .timeline-line {
        position: absolute;
        left: -24px;
        top: 20px;
        width: 2px;
        height: 100%;
        background: linear-gradient(180deg,
            rgba(0, 240, 255, 0.5) 0%,
            rgba(0, 240, 255, 0.2) 50%,
            rgba(0, 240, 255, 0.5) 100%);
      }

      .card-content {
        background: linear-gradient(135deg,
            rgba(0, 30, 60, 0.8),
            rgba(0, 15, 35, 0.9));
        border: 1px solid rgba(0, 240, 255, 0.3);
        border-radius: 8px;
        padding: 12px 14px;
        backdrop-filter: blur(10px);
        transition: all 0.3s ease;
        overflow: hidden;
        word-break: break-word;

        // 时间标签
        .card-time {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(0, 240, 255, 0.15);

          i {
            font-size: 14px;
            color: rgba(0, 240, 255, 0.8);
          }

          .time-text {
            font-size: 11px;
            color: rgba(0, 240, 255, 0.8);
            font-family: "Courier New", monospace;
            font-weight: 500;
            letter-spacing: 0.5px;
          }
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;

          .device-info {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;

            .device-icon {
              font-size: 24px;
              filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.6));
              display: flex;
              align-items: center;
              justify-content: center;

              .svg-icon {
                width: 24px;
                height: 24px;
                filter: drop-shadow(0 0 5px rgba(0, 240, 255, 0.6));
              }
            }

            .device-details {
              display: flex;
              flex-direction: column;
              gap: 2px;

              .device-name {
                font-size: 15px;
                font-weight: 600;
                color: #fff;
                text-shadow: 0 0 5px rgba(0, 240, 255, 0.5);
                line-height: 1.2;
              }

              .device-id {
                font-size: 11px;
                color: rgba(0, 240, 255, 0.7);
                font-family: "Courier New", monospace;
                line-height: 1.2;
              }
            }
          }
        }

        .card-body {
          display: flex;
          flex-direction: column;
          gap: 8px;

          .error-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;
            font-size: 13px;
            line-height: 1.5;

            .error-label {
              color: rgba(0, 240, 255, 0.7);
              font-weight: 500;
              white-space: nowrap;
            }

            .error-code {
              font-family: "Courier New", monospace;
              color: #ff6b6b;
              font-weight: 600;
              background: rgba(255, 107, 107, 0.1);
              padding: 2px 8px;
              border-radius: 4px;
              white-space: nowrap;
            }

            .error-divider {
              color: rgba(0, 240, 255, 0.3);
              margin: 0 2px;
            }

            .error-desc {
              color: rgba(255, 255, 255, 0.9);
              flex: 1;
              min-width: 150px;
            }

            .error-value {
              color: #00f0ff;
              font-size: 16px;
              font-weight: 700;
              font-family: "Courier New", monospace;
              text-shadow: 0 0 5px rgba(0, 240, 255, 0.5);
            }
          }
        }
      }

      // 在线状态卡片
      &.card-online .card-content {
        border-left: 3px solid #39ff14;
        box-shadow: 0 0 15px rgba(57, 255, 20, 0.2);
      }

      &.card-offline .card-content {
        border-left: 3px solid #ff2d2d;
        box-shadow: 0 0 15px rgba(255, 45, 45, 0.2);
      }

      // 错误等级卡片
      &.card-recovered .card-content {
        border-left: 3px solid #39ff14;
        box-shadow: 0 0 15px rgba(57, 255, 20, 0.2);
      }

      &.card-warn .card-content {
        border-left: 3px solid #ffa500;
        box-shadow: 0 0 15px rgba(255, 165, 0, 0.2);
      }

      &.card-error .card-content {
        border-left: 3px solid #ff6b6b;
        box-shadow: 0 0 15px rgba(255, 107, 107, 0.2);
      }

      &.card-fatal .card-content {
        border-left: 3px solid #ff2d2d;
        box-shadow: 0 0 20px rgba(255, 45, 45, 0.4);
        animation: cardPulse 2s ease-in-out infinite;
      }
    }

    .status-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        animation: blink 2s ease-in-out infinite;
      }

      &.status-online {
        background: rgba(57, 255, 20, 0.15);
        color: #39ff14;
        border: 1px solid rgba(57, 255, 20, 0.4);

        .status-dot {
          background: #39ff14;
          box-shadow: 0 0 5px #39ff14;
        }
      }

      &.status-offline {
        background: rgba(255, 45, 45, 0.15);
        color: #ff2d2d;
        border: 1px solid rgba(255, 45, 45, 0.4);

        .status-dot {
          background: #ff2d2d;
          box-shadow: 0 0 5px #ff2d2d;
        }
      }
    }

    .level-badge {
      padding: 4px 14px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.5px;

      &.level-recovered {
        background: rgba(57, 255, 20, 0.15);
        color: #39ff14;
        border: 1px solid rgba(57, 255, 20, 0.5);
        box-shadow: 0 0 10px rgba(57, 255, 20, 0.3);
      }

      &.level-warn {
        background: rgba(255, 165, 0, 0.2);
        color: #ffa500;
        border: 1px solid rgba(255, 165, 0, 0.5);
        box-shadow: 0 0 10px rgba(255, 165, 0, 0.3);
      }

      &.level-error {
        background: rgba(255, 107, 107, 0.2);
        color: #ff6b6b;
        border: 1px solid rgba(255, 107, 107, 0.5);
        box-shadow: 0 0 10px rgba(255, 107, 107, 0.3);
      }

      &.level-fatal {
        background: rgba(255, 45, 45, 0.25);
        color: #ff2d2d;
        border: 1px solid rgba(255, 45, 45, 0.6);
        box-shadow: 0 0 15px rgba(255, 45, 45, 0.5);
        animation: badgePulse 1.5s ease-in-out infinite;
      }
    }

    @keyframes pulse {

      0%,
      100% {
        transform: scale(1);
        opacity: 1;
      }

      50% {
        transform: scale(1.2);
        opacity: 0.8;
      }
    }

    @keyframes blink {

      0%,
      100% {
        opacity: 1;
      }

      50% {
        opacity: 0.3;
      }
    }

    @keyframes cardPulse {

      0%,
      100% {
        box-shadow: 0 0 20px rgba(255, 45, 45, 0.4);
      }

      50% {
        box-shadow: 0 0 30px rgba(255, 45, 45, 0.7);
      }
    }

    @keyframes badgePulse {

      0%,
      100% {
        box-shadow: 0 0 15px rgba(255, 45, 45, 0.5);
      }

      50% {
        box-shadow: 0 0 25px rgba(255, 45, 45, 0.8);
      }
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }

      to {
        transform: rotate(360deg);
      }
    }

    @keyframes ringPulse {

      0%,
      100% {
        transform: scale(0.8);
        opacity: 0.3;
      }

      50% {
        transform: scale(1.1);
        opacity: 0.6;
      }
    }

    @keyframes iconFloat {

      0%,
      100% {
        transform: translateY(0);
      }

      50% {
        transform: translateY(-10px);
      }
    }
  }
}
</style>
